import { useEffect, useState } from "react";
import SupplierSearch from "@/components/SupplierSearch";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtDateTime, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { PlusIcon, TrashIcon, XIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

/**
 * Solicitudes de órdenes de compra. Quien no genera órdenes (p. ej. Delegado) las solicita; el
 * Administrador aprueba o rechaza. Solo con la aprobación se habilita generar la orden (una vez).
 * Sin costos: la solicitud trabaja con productos y cantidades.
 */
const ESTADO = {
  pendiente: "border-agro-yellow/40 text-agro-yellow", aprobada: "border-agro-green/40 text-agro-green",
  rechazada: "border-red-500/40 text-red-300", generada: "border-blue-400/40 text-blue-300", generando: "border-border",
};

export default function PurchaseRequests() {
  const { user } = useAuth();
  const esAdmin = Boolean(user?.effective?.is_admin || user?.role === "admin");
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [ver, setVer] = useState(null);
  const load = () => api.get("/purchase-requests").then(({ data }) => setItems(data || [])).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const decidir = async (s, accion) => {
    const obs = window.prompt(accion === "aprobar" ? "Observaciones de la aprobación (opcional):" : "Motivo del rechazo:");
    if (obs === null) return;
    try { await api.post(`/purchase-requests/${s.id}/${accion}`, { observaciones: obs }); toast.success("Decisión registrada"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const generar = async (s) => {
    if (!window.confirm(`¿Generar la orden de compra de la solicitud ${s.numero}?`)) return;
    try { const { data } = await api.post(`/purchase-requests/${s.id}/generar-oc`); toast.success(`Orden ${data.oc_numero} generada`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Layout title="Solicitudes de compra" subtitle="Solicitudes de órdenes de compra para aprobación del Administrador"
      actions={<button onClick={() => setEdit({})} data-testid="new-purchase-request"
        className="bg-agro-green text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2"><PlusIcon size={16} /> Nueva solicitud</button>}>
      <div className="bg-card border border-border/60 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="purchase-requests-table">
          <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted"><tr>
            <th className="p-3 text-left">Solicitud</th><th className="p-3 text-left">Motivo</th><th className="p-3 text-left">Proveedor</th>
            <th className="p-3 text-left">Productos</th><th className="p-3 text-left">Estado</th><th className="p-3 text-left">Orden</th><th className="p-3"></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-text-muted">Sin solicitudes</td></tr>}
            {items.map((s) => (
              <tr key={s.id} className="border-t border-border/40 align-top">
                <td className="p-3"><div className="font-mono text-agro-green">{s.numero}</div><div className="text-[11px] text-text-muted">{fmtDateTime(s.fecha)} · {s.solicitante}</div></td>
                <td className="p-3 text-xs">{s.motivo}{s.decision?.observaciones && <div className="text-text-muted mt-1">Admin: {s.decision.observaciones}</div>}</td>
                <td className="p-3 text-xs">{s.proveedor_nombre || "—"}</td>
                <td className="p-3 text-xs">{s.items.map((i) => <div key={i.producto_id}>{i.cantidad} {i.unidad} · {i.descripcion}</div>)}</td>
                <td className="p-3"><span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${ESTADO[s.estado] || ""}`}>{s.estado}</span>
                  {s.version > 1 && <div className="text-[10px] text-text-muted">versión {s.version}</div>}</td>
                <td className="p-3 text-xs">{s.oc_numero ? <button onClick={() => setVer(s)} className="text-agro-green hover:underline">{s.oc_numero}</button> : "—"}</td>
                <td className="p-3 text-right whitespace-nowrap text-xs">
                  {esAdmin && s.estado === "pendiente" && <>
                    <button onClick={() => decidir(s, "aprobar")} data-testid={`pr-aprobar-${s.numero}`} className="text-agro-green hover:underline mr-2">Aprobar</button>
                    <button onClick={() => decidir(s, "rechazar")} className="text-red-300 hover:underline mr-2">Rechazar</button></>}
                  {s.estado === "aprobada" && <button onClick={() => generar(s)} data-testid={`pr-generar-${s.numero}`} className="text-agro-green hover:underline mr-2">Generar orden</button>}
                  {!["generada", "generando"].includes(s.estado) && (s.solicitante === user?.email || esAdmin) &&
                    <button onClick={() => setEdit(s)} className="text-text-muted hover:text-text-primary">Editar</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit && <EditarSolicitud s={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {ver && <VerOrden s={ver} onClose={() => setVer(null)} />}
    </Layout>
  );
}

function EditarSolicitud({ s, onClose, onSaved }) {
  const [f, setF] = useState({ motivo: s.motivo || "", proveedor_id: s.proveedor_id || "", fecha_requerida: s.fecha_requerida || "",
                               notas: s.notas || "", items: (s.items || []).map((i) => ({ producto_id: i.producto_id, cantidad: i.cantidad })) });
  const [prods, setProds] = useState([]);
  const [provs, setProvs] = useState([]);
  const [provEstado, setProvEstado] = useState("cargando");
  const [provError, setProvError] = useState("");
  const cargarProvs = () => {
    setProvEstado("cargando");
    api.get("/suppliers").then(({ data }) => { setProvs(data || []); setProvEstado("ok"); })
      .catch((e) => {
        setProvError(e?.response?.status === 403
          ? "Tu rol no consulta el catálogo de proveedores: el Administrador lo define al aprobar la solicitud."
          : "No se pudo cargar el catálogo de proveedores.");
        setProvEstado("error");
      });
  };
  useEffect(() => {
    api.get("/products").then(({ data }) => setProds(data || [])).catch(() => {});
    cargarProvs();
  }, []);
  const guardar = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, items: f.items.filter((i) => i.producto_id).map((i) => ({ ...i, cantidad: Number(i.cantidad) })) };
      if (s.id) {
        const { data } = await api.put(`/purchase-requests/${s.id}`, body);
        toast.success(data.estado === "pendiente" && s.estado === "aprobada" ? "Cambios guardados: requiere nueva aprobación" : "Solicitud actualizada");
      } else { await api.post("/purchase-requests", body); toast.success("Solicitud enviada al Administrador"); }
      onSaved();
    } catch (er) { toast.error(apiErr(er)); }
  };
  const inp = "w-full bg-app border border-border rounded-md px-3 py-2 text-sm";
  const m = useModalForm({ value: f, onClose: onClose, onSave: guardar });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" {...m.overlayProps}>
      <div role="dialog" className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="purchase-request-modal">
        <div className="p-4 border-b border-border flex justify-between"><h3 className="font-heading text-lg">{s.id ? `Solicitud ${s.numero}` : "Nueva solicitud de orden de compra"}</h3>
          <button aria-label="Cerrar" onClick={m.cerrar}><XIcon size={20} /></button></div>
        <form onSubmit={m.guardar} className="p-4 space-y-3">
          <div><label className="text-[10px] uppercase text-text-muted">Motivo *</label>
            <textarea rows={2} required value={f.motivo} onChange={(e) => setF({ ...f, motivo: e.target.value })} className={inp} data-testid="pr-motivo" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[10px] uppercase text-text-muted">Proveedor sugerido</label>
              <SupplierSearch suppliers={provs} estado={provEstado} mensajeError={provError} onRetry={cargarProvs}
                value={f.proveedor_id} onPick={(id) => setF({ ...f, proveedor_id: id })}
                seleccionado={s.proveedor_id ? { nombre: s.proveedor_nombre } : null} testid="pr-proveedor" />
              {f.proveedor_id && <button type="button" onClick={() => setF({ ...f, proveedor_id: "" })}
                className="text-[11px] text-text-muted hover:text-agro-yellow mt-1">Quitar (el Administrador lo define)</button>}</div>
            <div><label className="text-[10px] uppercase text-text-muted">Fecha requerida</label>
              <input type="date" value={f.fecha_requerida} onChange={(e) => setF({ ...f, fecha_requerida: e.target.value })} className={inp} /></div>
          </div>
          <div className="space-y-2">
            <label className="text-[10px] uppercase text-text-muted">Productos y cantidades *</label>
            {f.items.map((it, k) => (
              <div key={k} className="flex gap-2">
                <select value={it.producto_id} onChange={(e) => { const n = [...f.items]; n[k] = { ...it, producto_id: e.target.value }; setF({ ...f, items: n }); }}
                  className={`${inp} flex-1`} data-testid={`pr-prod-${k}`}>
                  <option value="">— Producto —</option>
                  {prods.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}</select>
                <input type="number" min="0.01" step="0.01" value={it.cantidad} onChange={(e) => { const n = [...f.items]; n[k] = { ...it, cantidad: e.target.value }; setF({ ...f, items: n }); }}
                  className="w-28 bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid={`pr-cant-${k}`} />
                <button aria-label="Eliminar" type="button" onClick={() => setF({ ...f, items: f.items.filter((_, j) => j !== k) })}><TrashIcon size={16} /></button>
              </div>
            ))}
            <button type="button" onClick={() => setF({ ...f, items: [...f.items, { producto_id: "", cantidad: 1 }] })} data-testid="pr-add-item"
              className="text-xs text-agro-green hover:underline">+ Agregar producto</button>
          </div>
          {s.estado === "aprobada" && <div className="text-xs text-agro-yellow">Cambiar proveedor, productos o cantidades requerirá una nueva aprobación.</div>}
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button type="button" onClick={m.cerrar} className="px-4 py-2 text-sm border border-border rounded-md">Cancelar</button>
            <button disabled={m.saving} type="submit" data-testid="pr-guardar" className="px-4 py-2 text-sm bg-agro-green text-black rounded-md font-medium">Guardar solicitud</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function VerOrden({ s, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/purchase-requests/${s.id}`).then(({ data }) => setD(data)).catch((e) => toast.error(apiErr(e))); }, [s.id]);
  const vm = useModalForm({ onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" {...vm.overlayProps}>
      <div role="dialog" className="bg-card border border-border rounded-lg w-full max-w-lg p-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg mb-2">Orden {d?.orden?.numero}</h3>
        <div className="text-xs text-text-muted mb-2">{d?.orden?.proveedor_nombre} · estado {d?.orden?.estado}</div>
        {(d?.orden?.items || []).map((i, k) => <div key={k} className="text-sm">{i.cantidad} · {i.codigo} {i.descripcion}</div>)}
        <div className="text-right mt-3"><button onClick={vm.cerrar} className="px-3 py-1.5 text-sm border border-border rounded">Cerrar</button></div>
      </div>
    </div>
  );
}
